import { useEffect, useState } from 'react'
import { Link2, Copy, Trash2, Plus } from 'lucide-react'
import { api } from '@/lib/api'

interface TriggerLink {
  id: string
  slug: string
  destinationUrl: string
  label: string | null
  expiresAt: string | null
  maxClicks: number | null
  currentClicks: number
  active: boolean
  updatedAt: string
}

export function TriggerLinksPage() {
  const [items, setItems] = useState<TriggerLink[]>([])
  const [draftSlug, setDraftSlug] = useState('')
  const [draftUrl, setDraftUrl] = useState('https://')
  const [draftLabel, setDraftLabel] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    const r = await api<{ items: TriggerLink[] }>('/v1/marketing/trigger-links')
    setItems(r.items)
  }

  useEffect(() => {
    void load()
  }, [])

  async function create(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      await api('/v1/marketing/trigger-links', {
        method: 'POST',
        body: JSON.stringify({
          slug: draftSlug,
          destinationUrl: draftUrl,
          label: draftLabel || undefined,
        }),
      })
      setDraftSlug('')
      setDraftUrl('https://')
      setDraftLabel('')
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function remove(t: TriggerLink) {
    if (!confirm(`¿Eliminar el link /t/${t.slug}?`)) return
    await api(`/v1/marketing/trigger-links/${t.id}`, { method: 'DELETE' })
    await load()
  }

  function copyShort(slug: string) {
    const url = `${window.location.origin.replace(/:\d+$/, '')}:4102/t/${slug}`
    navigator.clipboard.writeText(url).catch(() => undefined)
  }

  return (
    <div className="p-6 max-w-5xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <Link2 className="size-5 text-blueprint-700" />
          Trigger Links
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          URLs cortas con tracking de clicks · usables en snippets, firmas, SMS, WhatsApp · disponible en
          <span className="font-medium"> :4102/t/&lt;slug&gt;</span>
        </p>
      </header>

      <form onSubmit={(e) => void create(e)} className="bp-card p-3 mb-4 grid grid-cols-12 gap-2">
        <input
          value={draftSlug}
          onChange={(e) => setDraftSlug(e.target.value.toLowerCase())}
          placeholder="slug (p.ej. agenda)"
          required
          pattern="[a-z0-9-]+"
          className="col-span-2 border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
        />
        <input
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          placeholder="destino https://..."
          required
          type="url"
          className="col-span-5 border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
        />
        <input
          value={draftLabel}
          onChange={(e) => setDraftLabel(e.target.value)}
          placeholder="etiqueta (opcional)"
          className="col-span-3 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
        />
        <button type="submit" className="bp-btn-primary col-span-2 justify-center" disabled={busy}>
          <Plus className="size-3" /> Crear
        </button>
      </form>
      {err && (
        <div className="bp-card p-2 mb-3 text-xs font-mono text-red-700 bg-red-50 border-red-200">
          {err}
        </div>
      )}

      <div className="bp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
            <tr>
              <th className="px-3 py-2 text-left">Slug</th>
              <th className="px-3 py-2 text-left">Destino</th>
              <th className="px-3 py-2 text-right">Clicks</th>
              <th className="px-3 py-2 text-right">Actualizado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-t border-ink-900/10">
                <td className="px-3 py-2 font-mono text-blueprint-700">/t/{t.slug}</td>
                <td className="px-3 py-2 text-xs truncate max-w-xs">
                  <a href={t.destinationUrl} target="_blank" rel="noreferrer">
                    {t.label ?? t.destinationUrl}
                  </a>
                </td>
                <td className="px-3 py-2 text-right font-mono">{t.currentClicks}</td>
                <td className="px-3 py-2 text-right font-mono text-xs text-ink-700/60">
                  {new Date(t.updatedAt).toLocaleString('es-ES')}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => copyShort(t.slug)}
                    className="bp-btn"
                    title="Copiar URL"
                  >
                    <Copy className="size-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void remove(t)}
                    className="bp-btn text-red-700 ml-1"
                    title="Eliminar"
                  >
                    <Trash2 className="size-3" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-xs text-ink-700/50">
                  Sin trigger links.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
